import * as React from 'react';
import { generateHTML } from '@tiptap/core';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Loader2 } from 'lucide-react';
import { resolveTiptapDocMediaUrls } from '@ai-content-hub/content-core';
import { cn } from '../../../lib/utils';
import {
    detectRichTextFormat,
    getMarkdownString,
} from '../../../lib/rich-text-detect';
import { getRichTextExtensions, normalizeMediaNodesToInline } from '../../../lib/tiptap';
import type { TiptapJSON } from '../../../lib/tiptap';
import { AUDIO_MINIMAL_BUTTON_ICONS_HTML } from '../../../lib/tiptap-audio';
import './tiptap.css';

export type RichTextViewerData = TiptapJSON | string | { markdown: string };

export interface RichTextViewerProps {
    data?: RichTextViewerData;
    className?: string;
}

const extensions = getRichTextExtensions();

const richTextViewerContentClass = cn(
    'rich-text-content tiptap-content break-words space-y-3 w-full max-w-[80%] prose max-w-none leading-relaxed',
    '[&_h1]:text-3xl [&_h1]:font-bold',
    '[&_h2]:text-2xl [&_h2]:font-bold',
    '[&_h3]:text-xl [&_h3]:font-semibold',
    '[&_h4]:text-lg [&_h4]:font-semibold',
    '[&_blockquote]:border-l-4 [&_blockquote]:border-primary/30 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground',
    '[&_pre]:rounded-lg [&_pre]:bg-muted/50 [&_pre]:p-4 [&_pre]:overflow-x-auto [&_pre]:border [&_pre]:border-border',
    '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg',
    // EAA-1772: <audio> has no intrinsic width (unlike <img>, which falls back to
    // its decoded pixel size). Inside a shrink-to-fit ancestor (e.g. MCQPlayer's
    // choice row — a flex item with no width of its own), `w-full` resolves
    // against that same indefinite containing block and collapses to 0: the
    // player still mounts and fetches the audio (confirmed via range requests)
    // but renders an invisible 0px-wide control bar — reported as "audio is not
    // available on answer". `min-w` participates directly in the ancestor's
    // shrink-to-fit width calculation, so it survives regardless of the
    // surrounding layout — the static-viewer analogue of the pinned inline-style
    // wrapper width AudioView.tsx already uses for the editor's NodeView.
    '[&_audio]:block [&_audio]:w-full [&_audio]:min-w-[280px] [&_audio]:max-w-[480px] [&_audio]:my-3 [&_audio]:rounded-lg',
    '[&_iframe]:block [&_iframe]:border-0 [&_iframe]:rounded-lg [&_iframe]:max-w-full [&_iframe]:my-3',
    '[&_a]:text-primary [&_a]:underline',
    '[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:pl-4 [&_ul]:space-y-1 [&_ul]:list-outside',
    '[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:pl-4 [&_ol]:space-y-1 [&_ol]:list-outside',
    '[&_li]:pl-0.5',
);

const richTextViewerTiptapClass = cn(
    richTextViewerContentClass,
    '[&_th]:text-start [&_td]:text-start',
);

const TiptapRichTextHtml: React.FC<{ doc: TiptapJSON; className?: string }> = ({
    doc,
    className,
}) => {
    const [html, setHtml] = React.useState<string | null>(null);
    const [resolving, setResolving] = React.useState(true);

    const docKey = React.useMemo(() => JSON.stringify(doc), [doc]);

    React.useEffect(() => {
        let cancelled = false;
        setResolving(true);
        setHtml(null);
        void (async () => {
            try {
                const resolvedDoc = await resolveTiptapDocMediaUrls(doc);
                // EAA-1736: migrate legacy block media so the inline schema
                // renders it (generateHTML validates against the schema).
                const nextHtml = generateHTML(
                    normalizeMediaNodesToInline(resolvedDoc),
                    extensions
                );
                if (!cancelled) {
                    setHtml(nextHtml);
                }
            } catch {
                if (!cancelled) {
                    setHtml(null);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [docKey, doc]);

    const containerRef = React.useRef<HTMLDivElement>(null);

    // EAA-1736: wire up the "minimal" audio style's play/pause button. This
    // viewer renders plain HTML via dangerouslySetInnerHTML (no React tree per
    // node, unlike the editor's AudioView), so a raw DOM effect is the only way
    // to make the button actually toggle playback and swap its icon.
    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const buttons = Array.from(
            container.querySelectorAll<HTMLButtonElement>('button[data-audio-toggle]')
        );
        const cleanups: Array<() => void> = [];

        buttons.forEach((button) => {
            if (button.childElementCount === 0) {
                button.innerHTML = AUDIO_MINIMAL_BUTTON_ICONS_HTML;
            }
            const audio = button.previousElementSibling;
            if (!(audio instanceof HTMLAudioElement)) return;

            const setPlaying = () => button.setAttribute('data-playing', 'true');
            const setPaused = () => button.removeAttribute('data-playing');
            const handleClick = () => {
                if (audio.paused) void audio.play();
                else audio.pause();
            };

            audio.addEventListener('play', setPlaying);
            audio.addEventListener('pause', setPaused);
            audio.addEventListener('ended', setPaused);
            button.addEventListener('click', handleClick);

            cleanups.push(() => {
                audio.removeEventListener('play', setPlaying);
                audio.removeEventListener('pause', setPaused);
                audio.removeEventListener('ended', setPaused);
                button.removeEventListener('click', handleClick);
            });
        });

        return () => cleanups.forEach((fn) => fn());
    }, [html]);

    if (resolving) {
        return (
            <div className={cn(richTextViewerTiptapClass, 'flex items-center gap-2', className)}>
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!html || html === '<p></p>') return null;

    return (
        <div
            ref={containerRef}
            className={cn(richTextViewerTiptapClass, className)}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

export const RichTextViewer: React.FC<RichTextViewerProps> = ({
    data,
    className,
}) => {
    if (data == null) return null;

    const format = detectRichTextFormat(data);

    if (format === 'markdown') {
        const markdownStr = getMarkdownString(data);
        if (markdownStr == null || markdownStr.trim() === '') return null;
        return (
            <div className={cn(richTextViewerContentClass, className)}>
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{markdownStr}</ReactMarkdown>
            </div>
        );
    }

    if (format === 'tiptap') {
        const doc = data as TiptapJSON;
        if (!doc?.content || doc.content.length === 0) return null;
        return <TiptapRichTextHtml doc={doc} className={className} />;
    }

    return null;
};

RichTextViewer.displayName = 'RichTextViewer';
